import {onError} from '@apollo/client/link/error';

import notifier from '@albert-io/notifier';

/**
 * Keys to strip from GraphQL variables before sending to Honeybadger,
 * as a safeguard against PII leaking into error reports.
 * Aligned with the CONTENT_FILTERS list in the Honeybadger browser config,
 * plus camelCase variants used in GraphQL variable conventions.
 */
const SENSITIVE_VARIABLE_KEYS = new Set([
  'token',
  'email',
  'username',
  'password',
  'oldPassword',
  'newPassword',
  'verify_old_password',
  'verifyOldPassword',
  'password_reset_link',
  'passwordResetLink',
  'verify_password_reset_link',
  'verifyPasswordResetLink',
  'stripe_customer_id',
  'stripeCustomerId',
  'first_name',
  'firstName',
  'last_name',
  'lastName',
  'salutation',
  'consent_email',
  'consentEmail'
]);

/**
 * Recursively strips known-sensitive keys from a value.
 * Handles nested objects and arrays at any depth.
 */
function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) =>
        SENSITIVE_VARIABLE_KEYS.has(key) ? [key, '[FILTERED]'] : [key, sanitizeValue(val)]
      )
    );
  }

  return value;
}

/**
 * Recursively strips known-sensitive keys from a variables object.
 * Returns a deep copy with sensitive values replaced by '[FILTERED]'.
 */
export function sanitizeVariables(
  variables: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!variables) return undefined;

  return sanitizeValue(variables) as Record<string, unknown>;
}

/**
 * Apollo error link that reports GraphQL response errors and network errors
 * to Honeybadger for monitoring.
 *
 * - Tagged `graphql-error` (+ `graphql-network-error` for network failures)
 * - Component: `apollo-graphql`
 * - Fingerprinted by operation name for easy grouping
 *
 * This link never swallows errors — they still propagate to components.
 */
const errorLink = onError(({graphQLErrors, networkError, operation}) => {
  const operationName = operation.operationName || 'UnknownOperation';
  const operationDefinition = operation.query.definitions.find(
    (def) => def.kind === 'OperationDefinition'
  );
  const operationType =
    operationDefinition?.kind === 'OperationDefinition' ? operationDefinition.operation : 'unknown';

  if (graphQLErrors) {
    graphQLErrors.forEach((gqlError) => {
      notifier.notify(new Error(gqlError.message), {
        message: `GraphQL error in ${operationName}: ${gqlError.message}`,
        tags: ['graphql-error'],
        component: 'apollo-graphql',
        fingerprint: `graphql-error-${operationName}`,
        context: {
          operationName,
          operationType,
          errorMessage: gqlError.message,
          errorPath: gqlError.path,
          errorExtensions: gqlError.extensions,
          variables: sanitizeVariables(operation.variables)
        }
      });
    });
  }

  if (networkError) {
    const statusCode = 'statusCode' in networkError ? networkError.statusCode : undefined;

    notifier.notify(networkError, {
      message: `GraphQL network error in ${operationName}: ${networkError.message}`,
      tags: ['graphql-error', 'graphql-network-error'],
      component: 'apollo-graphql',
      fingerprint: `graphql-network-error-${operationName}`,
      context: {
        operationName,
        operationType,
        statusCode,
        networkErrorMessage: networkError.message,
        variables: sanitizeVariables(operation.variables)
      }
    });
  }
});

export default errorLink;
